// API Service for interacting with the Cloudflare Worker backend

import { supabase } from '../lib/supabaseClient'; // Import supabase client to get JWT

// Export for testing purposes
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

if (!API_BASE_URL) {
  console.error("REACT_APP_API_BASE_URL is not defined in the environment variables. Check your .env file.");
  // Optionally throw an error or provide a default fallback for local dev if needed
}

/**
 * Helper function to get the current Supabase JWT access token.
 * @returns {Promise<string|null>} The access token or null if not logged in.
 */
const getAccessToken = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
};

/**
 * Gets a pre-signed PUT URL from the backend and uploads the file directly to R2.
 * @param {File} imageFile The image file to upload.
 * @returns {Promise<string>} The R2 key of the uploaded file.
 */
export const uploadImage = async (imageFile) => {
  // Check for API_BASE_URL first
  if (!API_BASE_URL) {
    throw new Error("API Base URL is not configured.");
  }
  const token = await getAccessToken();
  if (!token) {
    throw new Error("User not authenticated.");
  }
   if (!imageFile) {
    throw new Error("No image file provided for upload.");
  }

  const getUrlEndpoint = `${API_BASE_URL}/api/generate/upload-url`;

  try {
    // 1. Get the pre-signed PUT URL and R2 key from the backend
    const urlResponse = await fetch(getUrlEndpoint, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            filename: imageFile.name, // Send filename hint
            contentType: imageFile.type || 'application/octet-stream', // Send content type
        }),
    });

    if (!urlResponse.ok) {
        let errorData;
        try { errorData = await urlResponse.json(); } catch (e) { /* Ignore */ }
        const errorMessage = errorData?.message || `Failed to get upload URL: ${urlResponse.status}`;
        throw new Error(errorMessage);
    }

    const { success, signedPutUrl, r2Key, message } = await urlResponse.json();

    if (!success || !signedPutUrl || !r2Key) {
        throw new Error(message || 'Invalid response format when getting upload URL.');
    }

    // 2. Upload the file directly to the pre-signed URL using PUT
    const uploadResponse = await fetch(signedPutUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': imageFile.type || 'application/octet-stream',
            // Do NOT include Authorization header for R2 pre-signed URL
        },
        body: imageFile,
    });

    if (!uploadResponse.ok) {
        // Attempt to get error details from R2 response (might be XML)
        const errorText = await uploadResponse.text();
        console.error("R2 Upload Error Response:", errorText);
        throw new Error(`Failed to upload file directly to R2: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }

    // If upload is successful, return the R2 key
    return r2Key;

  } catch (error) {
    console.error(`Error in uploadImage process for ${imageFile.name}:`, error);
    throw error;
  }
};


/**
 * Calls the generate endpoint with R2 keys of previously uploaded images.
 * @param {string[]} r2Keys An array of R2 keys for the input images.
 * @returns {Promise<any>} The JSON response containing generationId and generated R2 keys.
 */
export const generateImages = async (r2Keys) => {
  // Check for API_BASE_URL first
  if (!API_BASE_URL) {
    throw new Error("API Base URL is not configured.");
  }
  const token = await getAccessToken();
  if (!token) {
    throw new Error("User not authenticated.");
  }
   if (!r2Keys || r2Keys.length === 0) {
    throw new Error("No R2 keys provided for generation.");
  }

  const url = `${API_BASE_URL}/api/generate`; // Updated endpoint

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json', // Sending JSON body
      },
      body: JSON.stringify({ inputR2Keys: r2Keys }), // Send keys in JSON body
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) { /* Ignore */ }
      const errorMessage = errorData?.message || `Generation request failed: ${response.status}`;
      throw new Error(errorMessage);
    }

    // Response now contains { generationId, inputR2Keys, generatedImageUrls (which are keys) }
    return await response.json();
  } catch (error) {
    console.error("Error calling generateImages API:", error);
    throw error;
  }
};

/**
 * Fetches the user's generation history from the backend.
 * @returns {Promise<any[]>} An array of history items.
 */
export const getGenerationHistory = async () => {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("User not authenticated.");
  }
  if (!API_BASE_URL) {
    throw new Error("API Base URL is not configured.");
  }

  const url = `${API_BASE_URL}/api/generations`; // History endpoint

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      let errorData;
      try { errorData = await response.json(); } catch (e) { /* Ignore */ }
      const errorMessage = errorData?.message || `Failed to fetch history: ${response.status}`;
      throw new Error(errorMessage);
    }

    // Response is an array of history items:
    // [{ generationId, createdAt, inputR2Uris, generatedImageUrls (keys) }]
    return await response.json();
  } catch (error) {
    console.error("Error calling getGenerationHistory API:", error);
    throw error;
  }
};

// TODO: Add functions for other endpoints if needed (e.g., task management if you keep it)
